import { ProblemDetailsSchema, routeParId, type CodeErreur } from './contrat.js';

/**
 * Client de l’API Lakou, piloté par les routes du contrat : chemin, méthode, Idempotency-Key sur tout POST
 * non exempté, If-Match sur tout PATCH, erreurs problem+json traduites en phrases françaises (UX-26).
 */

const MESSAGES: Readonly<Record<CodeErreur, string>> = {
  VALIDATION_FAILED: 'Certaines informations manquent ou sont invalides. Vérifiez les champs signalés, puis réessayez.',
  UNAUTHENTICATED: 'Votre session a pris fin. Reconnectez-vous pour continuer.',
  FORBIDDEN: 'Vous n’avez pas les droits nécessaires pour cette page. Si vous pensez qu’il s’agit d’une erreur, contactez le Cabinet MAAT.',
  NOT_FOUND: 'Cet élément est introuvable. Il a peut-être été déplacé ou supprimé ; revenez à l’accueil pour le retrouver.',
  MANDATE_REQUIRED: 'Un mandat signé est nécessaire avant cette étape. Faites signer le mandat au dossier, puis recommencez.',
  IMPERSONATION_READ_ONLY: 'Cette vue est en lecture seule. Revenez à votre propre vue pour modifier quoi que ce soit.',
  CONSENT_MISSING: 'Le consentement de la personne n’est pas enregistré. Recueillez-le avant de poursuivre.',
  CONTACT_RESTRICTED: 'Cette personne ne souhaite pas être contactée. Aucun message ne peut lui être adressé.',
  WEEKLY_CAP_REACHED: 'Le nombre maximal de relances de la semaine est atteint. Reprenez lundi, ou ajustez la cadence du dossier.',
  OUTSIDE_SENDING_HOURS: 'Ce message ne peut pas partir à cette heure. Il sera proposé de nouveau pendant les heures d’envoi.',
  VERSION_CONFLICT: 'Cet élément a été modifié par une autre personne entre-temps. Rechargez la page pour voir sa dernière version.',
  PRECONDITION_REQUIRED: 'La version de l’élément à modifier est manquante. Rechargez la page, puis recommencez.',
  IDEMPOTENCY_KEY_REUSED: 'Cet envoi a déjà été pris en compte avec un contenu différent. Rechargez la page avant de recommencer.',
  CONFIRMATION_REQUIRED: 'Cette action demande une confirmation. Relisez ce qui va se passer, puis confirmez.',
  OTP_REQUIRED: 'Un code de vérification est demandé. Saisissez le code que vous avez reçu pour continuer.',
  LINK_EXPIRED: 'Ce lien n’est plus valable. Demandez-en un nouveau au Cabinet MAAT.',
  SIGNATURE_INVALID: 'La signature de cette demande n’a pas pu être vérifiée. Elle a été ignorée par sécurité.',
  INVOICE_IMMUTABLE: 'Cette facture est émise et ne peut plus être modifiée. Établissez un avoir si une correction est nécessaire.',
  RATE_LIMITED: 'Trop de demandes en peu de temps. Patientez quelques minutes, puis réessayez.',
};

const MESSAGE_RESEAU = 'Le réseau est indisponible. Votre saisie est conservée sur cet appareil ; réessayez dès que la connexion revient.';
const MESSAGE_INCONNU = 'Une erreur est survenue de notre côté. Réessayez dans un instant ; si elle persiste, contactez le Cabinet MAAT.';

export const messagePourCode = (code: CodeErreur): string => MESSAGES[code];

export type CodeErreurClient = CodeErreur | 'RESEAU' | 'INCONNUE';

export class ErreurApi extends Error {
  /** Detail du serveur : peut nommer le mandant ou un montant. Non énumérable, jamais dans `message`. */
  declare readonly detail?: string;

  constructor(
    readonly status: number,
    readonly code: CodeErreurClient,
    readonly messageUtilisateur: string,
    detail?: string,
  ) {
    super(messageUtilisateur);
    this.name = 'ErreurApi';
    Object.defineProperty(this, 'detail', { value: detail, enumerable: false });
  }
}

export interface EntreeAppel {
  parametres?: Record<string, string>;
  requete?: Record<string, string>;
  corps?: unknown;
  /** row_version attendue (If-Match), obligatoire pour les PATCH. */
  versionLigne?: number;
}

export interface Client {
  appeler<T = unknown>(routeId: string, entree?: EntreeAppel): Promise<T>;
}

export interface OptionsClient {
  fetch: typeof fetch;
  baseUrl?: string;
  genererCle?: () => string;
}

const cleParDefaut = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;

function construireChemin(chemin: string, parametres: Record<string, string>): string {
  return chemin.replace(/\{([a-zA-Z]+)\}/g, (_tout, nom: string) => {
    const valeur = parametres[nom];
    if (valeur === undefined) throw new Error(`Paramètre de chemin manquant : ${nom}`);
    return encodeURIComponent(valeur);
  });
}

/**
 * Limite de débit : la durée annoncée est celle du serveur (Retry-After, en secondes), arrondie à la minute supérieure.
 * Sans valeur lisible, aucune durée n'est promise (six personas ont lu « une minute » pour un blocage de 4 à 12 minutes).
 */
function messageLimite(reponse: Response): string {
  const secondes = Number(reponse.headers.get('retry-after'));
  if (!Number.isFinite(secondes) || secondes <= 0) return MESSAGES.RATE_LIMITED;
  const minutes = Math.ceil(secondes / 60);
  return `Trop de demandes en peu de temps. Réessayez dans ${minutes} minute${minutes > 1 ? 's' : ''}.`;
}

async function lireErreur(reponse: Response): Promise<ErreurApi> {
  try {
    const corps: unknown = await reponse.json();
    // Le service worker répond 503 { code: 'HORS_LIGNE' } quand le réseau manque : c'est une coupure, pas une panne du serveur.
    // L'écran garde alors la saisie et propose de réessayer au retour du réseau (PER-3-04).
    if (reponse.status === 503 && (corps as { code?: unknown } | null)?.code === 'HORS_LIGNE') return new ErreurApi(0, 'RESEAU', MESSAGE_RESEAU);
    const probleme = ProblemDetailsSchema.safeParse(corps);
    if (probleme.success) {
      const message = probleme.data.code === 'RATE_LIMITED' ? messageLimite(reponse) : messagePourCode(probleme.data.code);
      return new ErreurApi(reponse.status, probleme.data.code, message, probleme.data.detail);
    }
  } catch {
    // Corps absent ou illisible : repli ci-dessous.
  }
  return new ErreurApi(reponse.status, 'INCONNUE', MESSAGE_INCONNU);
}

export function creerClient(options: OptionsClient): Client {
  const base = options.baseUrl ?? '';
  const genererCle = options.genererCle ?? cleParDefaut;
  return {
    async appeler<T>(routeId: string, entree: EntreeAppel = {}): Promise<T> {
      const route = routeParId(routeId);
      let url = base + construireChemin(route.chemin, entree.parametres ?? {});
      if (entree.requete && Object.keys(entree.requete).length > 0) url += `?${new URLSearchParams(entree.requete).toString()}`;
      const entetes: Record<string, string> = { accept: 'application/json, application/problem+json' };
      if (entree.corps !== undefined) entetes['content-type'] = 'application/json';
      if (route.idempotence) entetes['Idempotency-Key'] = genererCle();
      if (route.siMatch) {
        if (entree.versionLigne === undefined) throw new Error(`Version de la ressource manquante pour ${routeId} (If-Match)`);
        entetes['If-Match'] = String(entree.versionLigne);
      }
      let reponse: Response;
      try {
        reponse = await options.fetch(url, {
          method: route.methode.toUpperCase(),
          headers: entetes,
          credentials: 'same-origin',
          ...(entree.corps !== undefined ? { body: JSON.stringify(entree.corps) } : {}),
        });
      } catch {
        throw new ErreurApi(0, 'RESEAU', MESSAGE_RESEAU);
      }
      if (!reponse.ok) throw await lireErreur(reponse);
      if (reponse.status === 204) return undefined as T;
      return (await reponse.json()) as T;
    },
  };
}
